import _ from 'lodash'
import './resourceList.scss'
import templateUrl from './resourceList.html'

/**
 * Resource List component.
 *
 * @example
 * <tmr-resource-list
 *   resources-count="::$ctrl.resourcesCount"
 *   current-page="::$ctrl.currentPage"
 *   current-page-size="::$ctrl.resources.length"
 *   page-size="::$ctrl.pageSize"
 *   export-action=""
 *   hide-export="true"
 *  ></tmr-resource-list>
 *
 * @type {ngComponent}
 * @prop {number}  resources-count   The total number results
 * @prop {number}  current-page      The page number (starting from zero)
 * @prop {number}  current-page-size The number results in the current page
 * @prop {number}  page-size         The page size
 * @prop {array<number>=} [page-size-options=[25, 50, 100, 250]] An optional custom list of page size options
 * @prop {array=}  breadcrumbs       An array of breadcrumbs for the toolbar. If omitted, defaults to route title
 * @prop {boolean} [header=true]     If false, the toolbar and the search form are omitted
 * @prop {boolean|string} [pager=true] If false, the pager is omitted. Routes with multiple pagers (that is: with an array as value of the `route.pager` option) may pass a string that will be used as pager id
 * @prop {string=} [export-action="export"]   The resource export query. See {@link TmrResource} for the default `exportQuery`
 * @prop {boolean} [hide-header=false]
 * @prop {boolean} [hide-add-button=false]
 * @prop {boolean} [hide-export=false]
 * @prop {boolean} [hide-refresh-button=false]
 * @prop {boolean=} [sticky-header=true]
 * @todo Document usage and transclusion of headers and resources
 */
export const tmrResourceList = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrResourceListController',
  bindings: {
    resourcesCount: '<',
    currentPage: '<',
    currentPageSize: '<',
    pageSize: '<',
    pageSizeOptions: '<?',
    breadcrumbs: '<?',
    header: '<',
    pager: '<',
    exportAction: '@?',
    hideHeader: '<?',
    hideAddButton: '<',
    hideExport: '<?',
    hideRefreshButton: '<',
    stickyHeader: '<?',
  },
  transclude: {
    filters: '?tmrResourceFilter',
    'list-header': 'tmrResourceListTh',
    list: '?tmrResource',
    'list-top': '?tmrResourceListTop',
    'list-bottom': '?tmrResourceListBottom',
  },
}

/**
 * Controller of component {@link tmrResourceList}.
 *
 * @type {ngController}
 */
export class tmrResourceListController {
  static ngType = 'controller'

  constructor(
    $rootElement,
    $scope,
    $state,
    $stateParams,
    $transclude,
    TmrPaging,
    TmrSearchFormFilters,
    UserStorage
  ) {
    'ngInject'
    Object.assign(this, {
      $rootElement,
      $scope,
      $state,
      $stateParams,
      $transclude,
      UserStorage,
      TmrPaging,
      TmrSearchFormFilters,
      filters: [],
    })
  }

  $onInit() {
    // Default values.
    this.header = this.header ?? true
    this.pager = this.pager ?? true
    this.stickyHeader = this.stickyHeader ?? true
    this.isSearching = false
    // Has the search form if there is at least one filter.
    this.hasSearchForm = this.$transclude.isSlotFilled('filters')

    this.hasActiveFilters = this.TmrSearchFormFilters.activeParams.length > 0
    this.requireFilter =
      this.$state.current.resourceList?.requireFilter === true

    // Don't print wrappers if they are empty.
    this.hasListTop = this.$transclude.isSlotFilled('list-top')
    this.hasListBottom = this.$transclude.isSlotFilled('list-bottom')

    // Show the search form if state param forces.
    this.showSearchForm = !!this.$stateParams.showSearchForm
    // Unused for now.
    // @todo Initialize filters here instead of transcluded filters in tmr-resource-filters.
    this.buildSearchForm = this.showSearchForm || this.hasActiveFilters
    this.$rootElement.toggleClass('tmr-freeze-content', this.showSearchForm)

    // Event is bound to allow override by extending classes.
    this.onKeyUpHandler = this.onKeyUp.bind(this)

    if (typeof this.pager === 'string') {
      this.currentPage = this.currentPage[this.pager]
      this.pageSize = this.pageSize[this.pager]
    }
  }

  $onDestroy() {
    this.$rootElement.removeClass('tmr-freeze-content')
    this.$rootElement.off('keyup', this.onKeyUpHandler)
  }

  /** Reset search. */
  searchFormReset() {
    return this.go({}, true, false)
  }

  /** Clear search. */
  searchFormClear() {
    return this.go({ showSearchForm: true }, true, false)
  }

  /**
   * Show the add button if there is an app.admin[resourceType].add state
   * and the user has access to it.
   */
  showAddButton() {
    return this.hideAddButton !== true && this.UserStorage.access('^.add')
  }

  /** React to changed page size. Reset offset to start from first page */
  onPageSizeChange(pageSize) {
    const limit = this.TmrPaging.limit(this.pager)
    const offset = this.TmrPaging.offset(this.pager)
    return this.$state.go(
      '.',
      { [limit]: pageSize, [offset]: 0 },
      { inherit: true }
    )
  }

  /**
   * Go to another page of the paginator
   */
  goToPage(page) {
    this.TmrPaging.go(
      page,
      typeof this.pager === 'string' ? this.pager : undefined
    )
  }

  /** Toggle search form. */
  searchFormToggle() {
    this.buildSearchForm = true
    this.showSearchForm = !this.showSearchForm
    this.$rootElement.toggleClass('tmr-freeze-content', this.showSearchForm)
    // Handle keyboard toggle with esc key.
    if (this.showSearchForm) this.$rootElement.on('keyup', this.onKeyUpHandler)
    else this.$rootElement.off('keyup', this.onKeyUpHandler)
  }

  /** Closes search form when esc is pressed */
  onKeyUp(event) {
    if (event.which === 27) this.$scope.$apply(() => this.searchFormToggle())
  }

  isSearchFormSubmitDisabled() {
    // With the requireFilter option, submit is allowed only if
    // at least one of the filters has a value
    if (this.requireFilter) {
      const defaults = this.TmrSearchFormFilters.paramsDefaultValues
      return Object.values(this.TmrSearchFormFilters._params).every(
        // Empty values are '', null, undefined, [] or equal to default value
        ({ param, value }) =>
          value === '' ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0) ||
          (defaults[param] !== undefined && _.isEqual(defaults[param], value))
      )
    }
  }

  /** Submit search form. */
  searchFormSubmit(reload) {
    // Reset paging.
    this.go(
      Object.assign(
        { offset: null, showSearchForm: false },
        this.TmrSearchFormFilters.updated
      ),
      reload
    )
  }

  /** Do a search. */
  go(params, reload = false, inherit = true) {
    this.isSearching = true
    this.$state
      .go('.', params, { reload: reload ? '.' : false, inherit })
      .finally(() => (this.isSearching = false))
  }
}
